import { createContext, useContext, useMemo, useState } from "react";
import useEvaApi, { EvaApiCall } from "../hooks/useEvaApi";
import useContextDefiner, {
  ContextDefinition,
} from "../hooks/useContextDefiner";
import useCustomEffect from "../hooks/useCustomEffect";
import { useAppContext } from "./AppContext";
import RandomizeHelper from "../helpers/RandomizeHelper";
import { getPortalVisitorFetchParams } from "../pages/portal/PortalPageHelpers";

const AuthContext = createContext();

export let STATIC_AUTH = null;
export let STATIC_UPDATE_AUTH = null;

export function AuthProvider({ children }) {
  const appContext = useAppContext();
  const callEvaApi = useEvaApi();
  const [auth, setAuth] = useState({
    user: null,
    token: null,
    portalToken: null,
    location: null,
    initialized: false,
    impersonating: false,
  });

  /**
   * This method will return the mapped locations of the user
   * @returns {object|null}
   */
  const mappedLocations = useMemo(() => {
    if (!auth.user) {
      return null;
    }

    const mappedLocations = {};
    auth.user.company.locations.forEach((location) => {
      mappedLocations[location.id] = location;
    });

    return mappedLocations;
  }, [auth.user]);

  /**
   * UseEffect for fetching the user when the component mounts
   */
  useCustomEffect(() => {
    handleAzureLogin();
    fetchUser();
  });

  /**
   * UseEffect for updating the location when the terminal changes
   */
  useCustomEffect(() => {
    if (appContext.contexts.frontofficeContext?.terminal) {
      updateLocation(appContext.contexts.frontofficeContext.terminal.location);

      // If the terminal has a specific language set, override the active language
      const terminalLanguageId =
        appContext.contexts.frontofficeContext.terminal.language;
      if (terminalLanguageId) {
        appContext.contexts.languageContext?.updateLanguage(terminalLanguageId);
      }
    }
  }, [appContext.contexts.frontofficeContext?.terminal]);

  /**
   * This method will update the auth state with the given callback and update the STATIC_AUTH variable
   * @param {function(prev) : object} callback
   */
  STATIC_UPDATE_AUTH = updateAuth;
  function updateAuth(callback) {
    setAuth((prev) => {
      //Call the callback to get the new auth object
      const newAuth = callback(prev);

      //Update the static auth variable
      STATIC_AUTH = newAuth;
      return newAuth;
    });
  }

  /**
   * This method will return the auth fetch params
   * @returns {object}
   */
  function getFetchParams(withVisitorParams = false) {
    return {
      with_user_company_data: true,
      with_company_locations_data: true,
      with_user_roles_data: true,
      with_role_permissions_data: true,
      ...(withVisitorParams ? getPortalVisitorFetchParams() : {}),
    };
  }

  /**
   * This method will fetch the user from the API
   */
  async function fetchUser() {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("url_token")) {
      await fetchPortalUser();
    } else {
      await callEvaApi(
        new EvaApiCall(`users/me`)
          .setLoadingGroup("APP")
          .setBlockAppRender(true)
          .setParams(getFetchParams())
          .setOnSuccess((response) => {
            //Update the state with the new user and location
            const newUser = response.data.data;
            updateAuth((prev) => ({
              ...prev,
              user: newUser,
              location: getUserLocation(newUser),
            }));
          })
          .setAlertError(false)
          .setAlertSuccess(false),
      );
    }

    updateAuth((prev) => ({
      ...prev,
      initialized: true,
    }));
  }

  /**
   * This method will fetch the portal user using the portal token from the URL params
   */
  async function fetchPortalUser() {
    const urlParams = new URLSearchParams(window.location.search);
    await callEvaApi(
      new EvaApiCall(`login/portal/${urlParams.get("url_token")}`)
        .setMethod("POST")
        .setLoadingGroup("APP")
        .setParams(getFetchParams(true))
        .setOnSuccess((response) => {
          //Update the state with the new user and location
          const newUser = response.data.data?.user;
          const newVisitor = response.data.data?.visitor;
          const newToken = response.data.data?.session_token ?? null;
          newUser.visitor = newVisitor;

          updateAuth((prev) => ({
            ...prev,
            user: newUser,
            location: newVisitor?.location ?? getUserLocation(newUser),
            portalToken: newToken,
          }));
        })
        .setAlertError(false)
        .setAlertSuccess(false),
    );
  }

  /**
   * This method will discover the user by their email
   * @param {string} email
   * @param {function} onSuccess
   * @param {function} onError
   */
  async function discover(email, onSuccess = null, onError = null) {
    await callEvaApi(
      new EvaApiCall("discover")
        .setMethod("POST")
        .setData({ email })
        .setLoadingGroup("discover")
        .setAlertSuccess(false)
        .setAlertError(true)
        .setOnError(onError)
        .setOnSuccess((response) => {
          const newDiscoverData = response.data.data;
          if (onSuccess) {
            onSuccess(newDiscoverData);
          }
        }),
    );
  }

  /**
   * This method will try to login to user with the given credentials
   * @param {string} email
   * @param {string} password
   * @param {string} tfaCode
   */
  async function login(
    email,
    password,
    tfaCode,
    onSuccess = null,
    onError = null,
  ) {
    await callEvaApi(
      new EvaApiCall("login")
        .setLoadingGroup("APP")
        .setParams(getFetchParams())
        .setMethod("POST")
        .setData({
          email: email,
          password: password,
          tfa_code: tfaCode,
        })
        .setOnSuccess((response) => {
          handleLogin(response, onSuccess);
        })
        .setOnError(onError)
        .setAlertError(true)
        .setAlertSuccess(false),
    );
  }

  /**
   * This method will switch the current authenticated user to the user with the given id
   * @param {int} userId
   * @param {function} onSuccess
   * @param {function} onError
   * @param {bool} saveSwitch If true, it will save the user we switched from in local storage
   */
  async function switchToUser(
    userId,
    onSuccess = null,
    onError = null,
    request = null,
    alertSuccess = true,
  ) {
    await callEvaApi(
      new EvaApiCall(request ?? `switch_user/${userId}`)
        .setLoadingGroup("APP")
        .setParams(getFetchParams())
        .setMethod("POST")
        .setOnSuccess((response) => {
          //Get the user and tokens from the response
          const newUser = response.data.data.user;
          const newTokens = response.data.data.tokens;

          //Update the state
          updateAuth((prev) => ({
            ...prev,
            user: newUser,
            token: newTokens.access.token,
            location: getUserLocation(newUser),
            impersonating: newTokens.impersonating ?? false,
          }));

          //Call the success callback if it exists
          if (onSuccess) onSuccess(response);
        })
        .setOnError(onError)
        .setAlertError(true)
        .setAlertSuccess(alertSuccess),
    );
  }

  /**
   * This method will switch the current authenticated user to a terminal user for the given company id
   * @param {int} companyId
   * @param {function} onSuccess
   * @param {function} onError
   */
  async function switchToTerminalUser(
    companyId,
    terminalId,
    onSuccess = null,
    onError = null,
  ) {
    if (!companyId || !terminalId) {
      return;
    }

    localStorage.setItem(
      "bc_terminal_id",
      appContext.contexts.frontofficeContext?.terminalId,
    );
    localStorage.setItem("frontoffice_terminal_id", terminalId);

    await switchToUser(
      null,
      () => {
        if (onSuccess) onSuccess();

        appContext.contexts.frontofficeContext?.updateTerminal(terminalId);
      },
      () => {
        if (onError) onError();

        localStorage.removeItem("bc_terminal_id");
        localStorage.removeItem("frontoffice_terminal_id");
      },
      `switch_user/terminal/${companyId}`,
      false,
    );
  }

  /**
   * This method will log the current user in to the company with the given id
   * @param {int} companyId
   */
  async function loginToCompany(companyId, onSuccess = null, onError = null) {
    await callEvaApi(
      new EvaApiCall(`login/${companyId}`)
        .setLoadingGroup("APP")
        .setParams(getFetchParams())
        .setMethod("POST")
        .setOnSuccess((response) => {
          //Update the state with the new user and location
          const newUser = response.data.data;
          updateAuth((prev) => ({
            ...prev,
            user: newUser,
            location: getUserLocation(newUser),
          }));

          //Call the onSuccess callback if it exists
          if (onSuccess) {
            onSuccess(response);
          }
        })
        .setOnError(onError)
        .setAlertError(true)
        .setAlertSuccess(false),
    );
  }

  /**
   * This method will try to logout the current authenticated user
   *
   * @returns {JSONResponse}
   */
  async function logout(onSuccess = null, onError = null) {
    const bcTerminalId = localStorage.getItem("bc_terminal_id");
    if (bcTerminalId) {
      localStorage.removeItem("bc_terminal_id");
      localStorage.setItem("frontoffice_terminal_id", bcTerminalId);
    }

    await callEvaApi(
      new EvaApiCall("logout")
        .setParams(getFetchParams())
        .setLoadingGroup("APP")
        .setMethod("POST")
        .setOnSuccess((response) => {
          //Get the new user, if it exists it means the user is still logged in
          const newUser = response.data.data?.user;
          const newToken = response.data.data?.tokens?.access?.token ?? null;
          updateAuth((prev) => ({
            ...prev,
            user: newUser,
            token: newUser ? (newToken ?? prev.token) : null,
            location: newUser ? getUserLocation(newUser) : null,
            impersonating: false,
          }));

          //If we returned to a bc terminal user, update the terminal
          if (bcTerminalId && newUser) {
            appContext.contexts.frontofficeContext?.updateTerminal(
              bcTerminalId,
            );
          }

          //Call the success callback if it exists
          if (onSuccess) {
            onSuccess(response);
          }
        })
        .setOnError(onError)
        .setAlertError(true)
        .setAlertSuccess(false),
    );
  }

  /**
   * This method will initialize the Azure login by generating the PKCE challenge and redirecting the user to the Azure login page
   * @param {object} azureConfig
   * @param {string} email
   */
  async function initAzureLogin(azureConfig, email) {
    const { verifier, challenge } = await RandomizeHelper.generatePKCE();
    const state = RandomizeHelper.generateRandomString(32);

    // Store the PKCE verifier and state in session storage to be used after the redirect
    sessionStorage.setItem("azure_pkce_verifier", verifier);
    sessionStorage.setItem("azure_oauth_state", state);
    sessionStorage.setItem("azure_config_id", azureConfig.id);
    sessionStorage.setItem("azure_user_email", email);
    sessionStorage.setItem("azure_search_params", window.location.search);

    const params = new URLSearchParams({
      client_id: azureConfig.client_id,
      redirect_uri: window.location.origin,
      response_mode: "query",
      response_type: "code",
      scope: "https://graph.microsoft.com/User.Read",
      state: state,
      code_challenge: challenge,
      code_challenge_method: "S256",
    });

    window.location.href = `https://login.microsoftonline.com/${azureConfig.tenant_id}/oauth2/v2.0/authorize?${params.toString()}`;
  }

  /**
   * This method will handle the Azure login by exchanging the authorization code for tokens and logging the user in
   */
  async function handleAzureLogin() {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");
    const state = urlParams.get("state");
    const storedState = sessionStorage.getItem("azure_oauth_state");
    const verifier = sessionStorage.getItem("azure_pkce_verifier");
    const azureConfigId = sessionStorage.getItem("azure_config_id");
    const azureUserEmail = sessionStorage.getItem("azure_user_email");
    const azureSearchParams =
      sessionStorage.getItem("azure_search_params") ?? "";
    if (!code || !state || state !== storedState || !verifier || !azureConfigId)
      return;

    sessionStorage.removeItem("azure_oauth_state");
    sessionStorage.removeItem("azure_pkce_verifier");
    sessionStorage.removeItem("azure_config_id");
    sessionStorage.removeItem("azure_user_email");
    sessionStorage.removeItem("azure_search_params");

    await callEvaApi(
      new EvaApiCall("login/azure")
        .setMethod("POST")
        .setData({
          code,
          verifier,
          redirect_uri: window.location.origin,
          azure_config_id: azureConfigId,
          email: azureUserEmail,
        })
        .setLoadingGroup("APP")
        .setBlockAppRender(true)
        .setAlertError(false)
        .setAlertSuccess(false)
        .setParams(getFetchParams())
        .setOnSuccess((response) => {
          window.location.href = `${window.location.origin}/login${azureSearchParams}`;
        }),
    );
  }

  /**
   * This method will handle the login response by updating the auth state and calling the onSuccess callback if it exists
   * @param {object} response
   * @param {function} onSuccess
   */
  async function handleLogin(response, onSuccess = null) {
    //Get the user and tokens from the response
    const newUser = response.data.data.user;
    const newTokens = response.data.data.tokens;

    //Update the state
    updateAuth((prev) => ({
      ...prev,
      user: newUser,
      token: newTokens.access.token,
      location: getUserLocation(newUser),
    }));

    //Call the success callback if it exists
    if (onSuccess) {
      onSuccess(response);
    }
  }

  /**
   * This method will return if the authenticated user has the given permission or not
   * @param {string} permission
   * @return {bool}
   */
  function hasPermission(permission) {
    if (!auth.user) {
      return false;
    }

    if (auth.user.role_type === "superadmin") {
      return true;
    }

    for (let i = 0; i < auth.user.roles.length; i++) {
      for (let j = 0; j < auth.user.roles[i].permissions.length; j++) {
        if (auth.user.roles[i].permissions[j].name === permission) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * This method will return the location of the given user
   * @param {object} user
   * @param {integer} locationId The location id if you do not want to use the local storage
   * @returns {integer|string}
   */
  function getUserLocation(user, locationId = undefined) {
    if (user?.location) {
      return user.location;
    }

    if (locationId === null) {
      return null;
    }

    return (
      user?.company?.locations?.find(
        (loc) =>
          parseInt(loc.id) ===
          (locationId ?? parseInt(localStorage.getItem("location_id"))),
      )?.id ?? null
    );
  }

  /**
   * This method will update the current location of the user
   * @param {integer|string} locationId
   */
  function updateLocation(locationId) {
    const newLocation = getUserLocation(auth.user, locationId);
    localStorage.setItem("location_id", newLocation);
    updateAuth((prev) => ({
      ...prev,
      location: newLocation,
    }));
  }

  return useContextDefiner(
    new ContextDefinition("authContext", AuthContext)
      .setData({
        auth,
        fetchUser,
        discover,
        login,
        switchToUser,
        switchToTerminalUser,
        initAzureLogin,
        loginToCompany,
        logout,
        hasPermission,
        updateLocation,
        mappedLocations,
      })
      .setChildren(children),
  );
}

export function useAuthContext() {
  return useContext(AuthContext);
}
